import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, X, Copy, MessageCircle, PenTool, ShieldCheck, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PlanSignatureLinkModal — pops open when the admin generates a signature
 * link for a fully-paid hechicería plan. Same visual style as the booking
 * link modal but branded for the "firmar" flow.
 */
export const PlanSignatureLinkModal = ({ plan, authHeader, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [linkData, setLinkData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!plan?.id) return undefined;
    let cancelled = false;
    setLoading(true); setError("");
    axios.post(
      `${API}/admin/payment-plans/${plan.id}/signature-link`,
      {}, { headers: authHeader() },
    ).then((r) => {
      if (cancelled) return;
      setLinkData(r.data);
    }).catch((e) => {
      if (cancelled) return;
      setError(e?.response?.data?.detail || "No se pudo generar el enlace de firma");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [plan?.id, authHeader]);

  const copyUrl = () => {
    if (!linkData?.url) return;
    navigator.clipboard.writeText(linkData.url)
      .then(() => toast.success("Enlace copiado"))
      .catch(() => toast.error("No se pudo copiar"));
  };

  const waLink = () => {
    if (!linkData?.url) return "#";
    const digits = String(linkData?.user?.phone || plan.user_phone || "").replace(/\D/g, "");
    const clientName = linkData?.user?.name || plan.user_name || "";
    const serviceName = linkData?.service?.name || plan.service_name || "tu trabajo";
    const msg = encodeURIComponent(
      `Hola ${clientName}, te comparto tu enlace para autorizar el status de tu ${serviceName}: ${linkData.url} Al entrar podrás firmar y confirmar tu identidad. Bendiciones.`
    );
    return digits ? `https://wa.me/${digits}?text=${msg}` : `https://wa.me/?text=${msg}`;
  };

  if (!plan) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[150] bg-black/85 backdrop-blur-md flex items-center justify-center px-3"
        data-testid="plan-signature-link-modal"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-md bg-gradient-to-br from-[#0a0510] to-black border-2 border-[#d4af37]/60 rounded-2xl p-5 shadow-[0_0_60px_rgba(212,175,55,0.35)]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            data-testid="plan-signature-link-close"
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-[#d4af37]" />
            <h3 className="font-serif text-lg text-white">Enlace de autorización + INE</h3>
          </div>

          <p className="text-xs text-neutral-400 mb-4">
            Cliente <b className="text-white">{plan.user_name}</b>
            {plan.user_visible_id ? <> · <span className="font-mono text-[#d4af37]">{plan.user_visible_id}</span></> : null}
            <br />
            Servicio: <b className="text-white">{plan.service_name}</b>
          </p>

          {loading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" />
              <div className="text-[11px] text-neutral-500 mt-2">Generando enlace de firma...</div>
            </div>
          ) : error ? (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              {error}
            </div>
          ) : linkData ? (
            <>
              <div className="rounded-xl border border-[#d4af37]/40 bg-black/60 px-3 py-3 mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/80 mb-1 flex items-center gap-1">
                  <PenTool className="w-3 h-3" /> Enlace de firma {linkData.reused ? "(reutilizado)" : "(nuevo)"}
                </div>
                <div className="text-[11px] text-white font-mono break-all select-all leading-relaxed" data-testid="plan-signature-link-url">
                  {linkData.url}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  data-testid="plan-signature-link-copy"
                  onClick={copyUrl}
                  className="btn-outline-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
                <a
                  data-testid="plan-signature-link-whatsapp"
                  href={waLink()}
                  target="_blank" rel="noreferrer"
                  className="btn-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Enviar por WhatsApp
                </a>
              </div>

              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2.5 text-[10px] text-emerald-200 flex gap-2 items-start">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>
                  El cliente firmará con touch, se registrará su ubicación,
                  IP y huella de dispositivo, y escaneará ambos lados de su
                  INE. Cuando termine podrás descargar el contrato PDF.
                </span>
              </div>
            </>
          ) : null}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PlanSignatureLinkModal;
